import { faqsPl } from "@data/cennik-mvp-landing/faq";
import {
  aboutUsCopyPl,
  contactFormPl,
  faqStructuredData,
  mvpClusterLinks,
  mvpKnowledgeBaseLinks,
  organizationStructuredData,
  serviceStructuredData,
} from "@data/mvp-landing/shared";
import {
  Calculator,
  Calendar,
  CheckCircle,
  Code,
  Cpu,
  FileText,
  Layers,
  PenTool,
  Rocket,
  Search,
  Smartphone,
  Sparkles,
  Wallet,
} from "lucide-react";

import type { FAQCopy } from "@components/bluetooth-landing/FAQ";
import type { FooterCopy } from "@components/bluetooth-landing/Footer";
import type { HeroCopy } from "@components/bluetooth-landing/Hero";
import type { ProcessCopy } from "@components/bluetooth-landing/Process";
import type { ServicesCopy } from "@components/bluetooth-landing/Services";
import type { TargetAudienceCopy } from "@components/bluetooth-landing/TargetAudience";
import type { ValuePropositionCopy } from "@components/bluetooth-landing/ValueProposition";

export const title =
  "Ile kosztuje MVP? Wycena aplikacji, urządzenia IoT lub platformy";

export const description =
  "Ile kosztuje MVP aplikacji, SaaS lub urządzenia IoT? Orientacyjny cennik MVP, widełki budżetowe i co najbardziej wpływa na wycenę. Polski software house — wycena po briefie lub warsztatach.";

export const structuredData = [
  organizationStructuredData(description),
  serviceStructuredData(
    "Cennik i wycena MVP",
    description,
    "MVP cost estimation",
  ),
  faqStructuredData(faqsPl),
];

export const heroCopy: HeroCopy = {
  header: "Ile kosztuje",
  headerTail: "MVP?",
  description:
    "Orientacyjne widełki kosztów MVP: aplikacja webowa, mobilna, SaaS z AI albo urządzenie IoT. Pokażemy, co winduje budżet i jak przygotować się do wyceny, żeby oferty się nie rozjeżdżały.",
  cta: {
    text: "Darmowa konsultacja",
    href: "#kontakt",
  },
  secondaryCta: {
    text: "Zobacz widełki",
    href: "#uslugi",
  },
  benefits: [
    { text: "Widełki web / mobile / IoT", Icon: CheckCircle },
    { text: "Co winduje koszt MVP", Icon: CheckCircle },
    { text: "Wycena po briefie lub warsztatach", Icon: CheckCircle },
  ],
};

export const targetAudienceCopy: TargetAudienceCopy = {
  header: {
    h2: "Kim są nasi klienci?",
    h3: "Dla kogo jest cennik i wycena MVP?",
    p: "Na pierwszym kontakcie pytanie „ile kosztuje MVP” pada niemal zawsze obok timeline’u, NDA i modelu współpracy. Chcesz orientacji budżetowej, zanim podpiszesz umowę — i słusznie.",
  },
  points: [
    {
      Icon: Wallet,
      title: "Szukasz orientacyjnych widełek przed rozmową z inwestorem",
      description:
        "Potrzebujesz rzędu wielkości: web MVP vs mobile vs IoT — żeby zaplanować budżet, nie „cenę z sufitu”.",
    },
    {
      Icon: FileText,
      title: "Masz brief i chcesz konkretną wycenę",
      description:
        "Po NDA i doprecyzowaniu zakresu przygotowujemy ofertę z harmonogramem. Im lepszy brief, tym węższe widełki.",
    },
    {
      Icon: Calculator,
      title: "Dostałeś wyceny, które różnią się o rząd wielkości",
      description:
        "Pomagamy zrozumieć, co jest w zakresie (a czego nie) i kiedy warsztaty produktowe są tańsze niż kolejna „wycena w ciemno”.",
    },
    {
      Icon: Sparkles,
      title: "Planujesz MVP z AI i nie wiesz, ile to kosztuje",
      description:
        "Integracja LLM to zwykle nie największy koszt — UI, auth, dane, panel i jakościowe edge case’y windują budżet bardziej niż samo API.",
    },
    {
      Icon: Cpu,
      title: "Porównujesz koszt MVP appki vs urządzenia",
      description:
        "Hardware zmienia ekonomię: DevKit vs własna PCB, firmware, certyfikacja. Pokazujemy, kiedy zacząć tanio od PoC.",
    },
  ],
  cta: {
    h4: "Chcesz wycenę pod swój zakres?",
    text: "Opisz pomysł albo prześlij brief — damy orientacyjne widełki albo zaproponujemy warsztaty przed ofertą.",
    href: "#kontakt",
    buttonText: "Umów darmową konsultację",
  },
};

export const valuePropositionCopy: ValuePropositionCopy = {
  header: {
    h2: "Nasza specjalizacja",
    h3: "Jak wyceniamy MVP uczciwie?",
    p: "Nie sprzedajemy „MVP za X zł” w ciemno. Dajemy widełki oparte o typowe zakresy projektów, a konkretną ofertę budujemy na briefie albo warsztatach — żebyś porównywał jabłka do jabłek.",
    imageAlt: "Cennik MVP — wycena aplikacji i urządzeń",
  },
  values: [
    {
      Icon: Layers,
      title: "Widełki per typ produktu",
      description:
        "Inaczej wycenia się PWA z dashboardem, inaczej Flutter + BLE, inaczej SaaS z płatnościami i AI.",
    },
    {
      Icon: Search,
      title: "Transparentne koszty ukryte",
      description:
        "App Store, utrzymanie, CE, produkcja PCB, monitoring 24/7 — mówimy wprost, co jest poza typowym MVP.",
    },
    {
      Icon: Calculator,
      title: "Stała cena lub Time & Material",
      description:
        "Dobieramy model do pewności zakresu. Często: stała cena na pierwszy etap, potem Time & Material na iteracje.",
    },
    {
      Icon: FileText,
      title: "Lepsza wycena po warsztatach",
      description:
        "Gdy brief jest niepełny, warsztaty za kilka–kilkanaście tysięcy zł chronią przed przepaleniem setek tysięcy.",
    },
    {
      Icon: CheckCircle,
      title: "Polski software house, budżety w PLN",
      description:
        "Wyceny i umowy po polsku, dane w UE, bezpośrednia komunikacja z zespołem — ważne na starcie produktu.",
    },
  ],
};

export const servicesCopy: ServicesCopy = {
  header: {
    h2: "Usługi",
    h3: "Orientacyjny cennik MVP — typowe widełki",
    p: "To nie jest cennik sklepowy. To rzędy wielkości z realnych projektów. Konkretna oferta zawsze wynika z Twojego zakresu.",
  },
  services: [
    {
      Icon: FileText,
      title: "Warsztaty produktowe — od kilku do kilkunastu tys. zł",
      description:
        "Discovery, user stories, ryzyka i materiały do wyceny. Najtańszy sposób na domknięcie zakresu przed developmentem.",
    },
    {
      Icon: Code,
      title: "MVP aplikacji webowej / PWA — zwykle 40–120 tys. zł",
      description:
        "Logowanie, kluczowa funkcja, prosty panel, wdrożenie. Górna półka: role, integracje, płatności, bardziej złożony admin.",
    },
    {
      Icon: Smartphone,
      title: "MVP aplikacji mobilnej — zwykle 80–200 tys. zł",
      description:
        "Flutter lub natywne iOS/Android. BLE, offline, powiadomienia i dwa store’y windują koszt.",
    },
    {
      Icon: Layers,
      title: "MVP SaaS / platformy — zwykle 100–250 tys. zł",
      description:
        "Multi-tenant lub role B2B, panel, API, płatności (Stripe, Przelewy24). Marketplace’y i skomplikowane rozliczenia — wyżej.",
    },
    {
      Icon: Sparkles,
      title: "MVP z AI / LLM — zwykle +20–80 tys. zł do zakresu bazowego",
      description:
        "Samo API modelu jest tanie; koszt generuje UX, dane, jakość odpowiedzi, limity i panel administracyjny.",
    },
    {
      Icon: Cpu,
      title: "MVP urządzenia IoT (DevKit) — zwykle 60–150 tys. zł",
      description:
        "Firmware + prosta app/dashboard na gotowej płytce. Własna PCB i pełny system — często 150–350 tys. zł+.",
    },
    {
      Icon: Wallet,
      title: "PoC techniczny — zwykle 30–80 tys. zł",
      description:
        "Wąska weryfikacja ryzyka (AI, BLE, integracja OEM) przed pełnym MVP — częsty start przy NDA.",
    },
    {
      Icon: Calculator,
      title: "Indywidualna wycena po briefie",
      description:
        "Harmonogram, model współpracy i widełki pod Twój zakres — po konsultacji lub warsztatach.",
    },
  ],
  cta: {
    h4: "Chcesz wycenę pod swój brief?",
    p: "Prześlij opis pomysłu albo umów konsultację — damy orientację budżetową w dni robocze.",
    button: {
      text: "Umów darmową konsultację",
      href: "#kontakt",
    },
  },
};

export const processCopy: ProcessCopy = {
  header: {
    h2: "Nasz Proces",
    h3: "Jak przebiega wycena MVP",
    p: "Od orientacyjnych widełek po ofertę. Im lepsze materiały wejściowe, tym precyzyjniejsza cena.",
  },
  stepNumber: "Krok",
  steps: [
    {
      Icon: Calendar,
      title: "Konsultacja",
      duration: "30 minut",
      description:
        "Omówienie pomysłu, etapu i budżetu. Dostajesz wstępny rząd wielkości i rekomendację następnego kroku.",
    },
    {
      Icon: FileText,
      title: "Brief lub warsztaty",
      duration: "kilka dni",
      description:
        "Przy niepełnym zakresie proponujemy warsztaty. Przy Figmie i user stories — od razu wycenę.",
    },
    {
      Icon: Search,
      title: "Analiza ryzyk i stacku",
      duration: "1–3 dni",
      description:
        "Integracje, AI, hardware, RODO — identyfikujemy czynniki kosztotwórcze.",
    },
    {
      Icon: Calculator,
      title: "Oferta z widełkami",
      duration: "kilka dni",
      description:
        "Zakres, harmonogram, stała cena lub Time & Material, kamienie milowe i założenia.",
    },
    {
      Icon: PenTool,
      title: "Doprecyzowanie",
      duration: "1–2 spotkania",
      description:
        "Pytania, warianty zakresu A/B, dopasowanie do twardego budżetu.",
    },
    {
      Icon: FileText,
      title: "NDA i umowa",
      duration: "ustalenie",
      description:
        "Jeśli potrzeba — NDA przed szczegółami IP, potem umowa na PoC lub MVP.",
    },
    {
      Icon: Rocket,
      title: "Start prac",
      duration: "od razu",
      description:
        "Start prac, dostęp do repozytorium, kanał komunikacji i pierwszy kamień milowy.",
    },
    {
      Icon: CheckCircle,
      title: "Kontrola budżetu w trakcie",
      duration: "na stałe",
      description:
        "Raportowanie postępu i wczesne ostrzeżenie, gdy zakres chce urosnąć poza MVP.",
    },
  ],
  cta: {
    href: "#kontakt",
    text: "Poproś o wycenę MVP",
  },
};

export const aboutUsCopy = aboutUsCopyPl({
  h3: "Polski software house — wyceny MVP bez ściemy",
  imageAlt: "Zespół Blues Brackets — cennik i wycena MVP",
});

export const faqCopy: FAQCopy = {
  header: {
    h2: "FAQ",
    h3: "Najczęściej zadawane pytania o koszt MVP",
  },
  faqs: faqsPl,
};

export const footerCopy: FooterCopy = {
  header: {
    h2: "Chcesz wiedzieć, ile może kosztować Twoje MVP?",
    p: "Umów się na darmową konsultację — damy orientacyjne widełki albo zaproponujemy warsztaty przed konkretną ofertą.",
  },
  cta: {
    href: "#kontakt",
    text: "Umów darmową konsultację",
    p: "Ile kosztuje MVP? Wycena aplikacji, SaaS i urządzeń IoT. Polski software house — widełki oparte o realny zakres.",
    email: "hello@bluesbrackets.com",
    phone: "+48 535 462 678",
  },
  links: {
    h4: "Na skróty",
    links: [
      { href: "#uslugi", text: "Widełki kosztów" },
      { href: "#proces", text: "Proces wyceny" },
      { href: "#faq", text: "FAQ" },
      { href: "/mvp", text: "Budowa MVP" },
      ...mvpClusterLinks.filter(
        l => l.href !== "/mvp" && l.href !== "/cennik-mvp",
      ),
    ],
  },
  knowledgeBase: {
    h4: "Baza wiedzy",
    links: [...mvpKnowledgeBaseLinks],
  },
  copyright: "bluesBrackets. Wszelkie prawa zastrzeżone.",
  privacyPolicy: "Polityka prywatności",
  privacyPolicyHref: "/policy",
  contactForm: contactFormPl,
};
